import type { ReactNode } from 'react'
import { Check } from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuItem,
} from '../ui/dropdown-menu'
import type { Server } from '../../types'
import { mediaUrl } from '../../services/config'
import { displayInitial } from '../../utils/displayInitial'

// Wraps the rail logo in a server-switcher dropdown (OSPROD-199). With a
// single reachable server the dropdown adds nothing, so we render the
// trigger child as-is and let its own onClick (Business OS home) stand.
// Global admins reach every server (Phase A GET /servers), so the list can
// be long — the content scrolls. Deferred (not silently dropped): a search
// filter for very long lists, and a per-row "Server settings" shortcut for
// MANAGE_SERVER users — the latter lands with the Phase C server-settings UI.
export function ServerSwitcher({
  servers,
  currentId,
  onSelect,
  children,
}: {
  servers: Server[]
  currentId: string | null
  onSelect: (id: string) => void
  children: ReactNode
}) {
  if (servers.length <= 1) return <>{children}</>

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>{children}</DropdownMenuTrigger>
      <DropdownMenuContent side="right" align="start" className="max-h-[70vh] overflow-y-auto w-56">
        <DropdownMenuLabel>Switch server</DropdownMenuLabel>
        {servers.map((s) => (
          <DropdownMenuItem key={s.id} onSelect={() => onSelect(s.id)} className="gap-2">
            <span className="w-6 h-6 rounded-md bg-muted shrink-0 flex items-center justify-center overflow-hidden text-[11px] font-semibold">
              {s.iconUrl ? (
                <img src={mediaUrl(s.iconUrl)} alt="" className="w-full h-full object-cover" />
              ) : (
                displayInitial(s.name)
              )}
            </span>
            <span className="flex-1 truncate">{s.name}</span>
            {s.id === currentId && <Check size={14} className="shrink-0 text-primary" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
